import React from 'react';
import { createPortal } from 'react-dom';
import {
  Box, Typography, IconButton,
  Chip, CircularProgress, Slide, Paper,
  Accordion, AccordionSummary, AccordionDetails,
  LinearProgress,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel,
  Collapse, Divider, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CodeIcon from '@mui/icons-material/Code';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import FlagIcon from '@mui/icons-material/Flag';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTheme, alpha } from '@mui/material/styles';

// ── Optional package: react-diff-viewer-continued ────────────────────────────
// Activated automatically once `npm install react-diff-viewer-continued` is run.
// Falls back to the built-in CodeBlock pair + custom line-diff when absent.
let ReactDiffViewer = null;
try {
  // eslint-disable-next-line
  ReactDiffViewer = require('react-diff-viewer-continued').default;
} catch (_) { /* not installed — custom fallback used */ }
import {
  PLAGIARISM_HIGH_HEX,
  PLAGIARISM_MEDIUM_HEX,
  PLAGIARISM_LOW_HEX,
  TABLE_BORDER,
  TABLE_PROGRESS_BG,
  TABLE_LABEL,
  TABLE_HEADER_BG,
  TABLE_HEADER_TEXT,
  TABLE_MUTED,
  INDIGO,
  INDIGO_LIGHT,
  INDIGO_LIGHTER,
  TOOLBAR_BG_DARK,
  TOOLBAR_BG_LIGHT,
  CODE_BG_DARK,
  CODE_GUTTER_BG,
  CODE_GUTTER_BG_DARK,
  CODE_FOLD_BG,
  CODE_GUTTER_COLOR,
  CODE_BLOCK_SHADOW,
  DIFF_REMOVED_TEXT,
  DIFF_ADDED_TEXT,
  DIFF_REMOVED_TEXT_LIGHT,
  DIFF_ADDED_TEXT_LIGHT,
  DIFF_NEUTRAL_TEXT,
  DIFF_NEUTRAL_TEXT_DARK,
  DIFF_REMOVED_BG,
  DIFF_ADDED_BG,
  DIFF_ADDED_GUTTER_BG,
  DIFF_REMOVED_GUTTER_BG,
  DIFF_WORD_ADDED_BG,
  DIFF_WORD_REMOVED_BG,
  PANEL_SHADOW,
  PANEL_CLOSE_BTN_BG,
  PANEL_CLOSE_BTN_HOVER,
} from '../../utils/colors';

const LANGUAGE_MAP = {
  'Python': 'python', 'Python3': 'python',
  'C++': 'cpp', 'C': 'c', 'Java': 'java', 'C#': 'csharp',
  'JavaScript': 'javascript', 'TypeScript': 'typescript',
  'Go': 'go', 'Rust': 'rust', 'Swift': 'swift',
  'Kotlin': 'kotlin', 'Ruby': 'ruby', 'Scala': 'scala',
  'PHP': 'php', 'MySQL': 'sql', 'MS SQL Server': 'sql', 'Oracle': 'sql',
};

const getPrismLanguage = (language) => LANGUAGE_MAP[language] ?? 'plaintext';

const PANEL_WIDTH = '85vw';

/** Dark theme sets `palette.background.paper` to transparent; drawers need a solid fill. */
const PANEL_SURFACE_DARK = '#0f172a';

// ── Status helper (mirrors PlagiarismResultsTable logic) ──────────────────────
const deriveStatus = (score, plagiarismStatus) => {
  if (plagiarismStatus) {
    if (plagiarismStatus === 'HIGH')   return { label: 'High Risk',   hex: PLAGIARISM_HIGH_HEX };
    if (plagiarismStatus === 'MEDIUM') return { label: 'Suspicious',  hex: PLAGIARISM_MEDIUM_HEX };
    return                                    { label: 'OK',          hex: PLAGIARISM_LOW_HEX };
  }
  if (score > 0.85) return { label: 'AI Suspected', hex: PLAGIARISM_HIGH_HEX };
  if (score >= 0.6) return { label: 'Suspicious',   hex: PLAGIARISM_MEDIUM_HEX };
  return                   { label: 'OK',           hex: PLAGIARISM_LOW_HEX };
};

// ── Shared Accordion styles ───────────────────────────────────────────────────
const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '8px !important',
  mb: 1.5,
  boxShadow: 'none',
  backgroundImage: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { mt: 0, mb: 1.5 },
};

const panelSurfaceBg = (t) =>
  t.palette.mode === 'dark' ? PANEL_SURFACE_DARK : t.palette.background.paper;

const panelAccordionSx = {
  ...accordionSx,
  bgcolor: panelSurfaceBg,
};

const accordionSummarySx = {
  px: 2,
  minHeight: 48,
  bgcolor: panelSurfaceBg,
  '&.Mui-expanded': { minHeight: 48 },
  '& .MuiAccordionSummary-content': { my: '10px' },
};

// ── Section header: icon + title + optional badge ────────────────────────────
const SectionHeader = ({ icon, title, badge }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
    {icon}
    <Typography fontWeight={600}>{title}</Typography>
    {badge}
  </Box>
);


const CodeBlock = ({ title, code, language = 'plaintext', accent }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const prismStyle = isDark ? atomDark : oneLight;
  const titleBg = accent ? `${accent}18` : (isDark ? 'rgba(255,255,255,0.06)' : theme.palette.grey[100]);
  const titleColor = accent ?? (isDark ? TABLE_LABEL : theme.palette.text.secondary);
  const gutterColor = isDark ? CODE_GUTTER_COLOR : 'rgba(15, 23, 42, 0.45)';
  const borderCol = accent ?? TABLE_BORDER;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          px: 1.5,
          py: 1.6,
          borderRadius: '6px 6px 0 0',
          bgcolor: titleBg,
          border: `1px solid ${borderCol}`,
          borderBottom: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ textTransform: 'uppercase', color: titleColor, letterSpacing: 0.6 }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          border: `1px solid ${borderCol}`,
          borderRadius: '0 0 6px 6px',
          overflow: 'hidden',
          maxHeight: 320,
          overflowY: 'auto',
        }}
      >
        <SyntaxHighlighter
          language={getPrismLanguage(language)}
          style={prismStyle}
          showLineNumbers
          wrapLongLines
          customStyle={{
            margin: 0,
            fontSize: '0.74rem',
          }}
          lineNumberStyle={{ color: gutterColor, minWidth: '2.2em' }}
        >
          {code?.trim() || '// No code available'}
        </SyntaxHighlighter>
      </Box>
    </Box>
  );
};


const AISimilarityReport = ({ report, studentCode, studentLanguage }) => {
  if (!report) {
    return (
      <Typography variant="body2" color="text.secondary">
        No plagiarism analysis report is available for this submission.
      </Typography>
    );
  }

  const { similarity, plagiarismStatus, breakdown, matchedSolution, isFlagged } = report;
  const score    = similarity ?? 0;
  const pct      = (score * 100).toFixed(1);
  const { label, hex } = deriveStatus(score, plagiarismStatus);

  // Source label: normalise to display-friendly form (ai → AI, github → GitHub, etc.)
  const sourceLabel = (() => {
    const s = (matchedSolution?.source ?? '').toLowerCase();
    if (s === 'ai')       return 'AI';
    if (s === 'github')   return 'GitHub';
    if (s === 'editorial') return 'Editorial';
    return matchedSolution?.source || 'Reference';
  })();

  return (
    <Box>
      {/* ── Similarity score bar ── */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Similarity Score
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: hex }}>
              {pct}%
            </Typography>
            <Chip
              label={label}
              size="small"
              sx={{
                height: 20, fontSize: 11, fontWeight: 700,
                bgcolor: hex + '22', color: hex, border: `1px solid ${hex}`,
              }}
            />
            {isFlagged && (
              <Chip
                label="Flagged"
                size="small"
                color="error"
                icon={<FlagIcon sx={{ fontSize: '14px !important' }} />}
                sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
              />
            )}
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(score * 100, 100)}
          sx={{
            height: 8,
            borderRadius: 99,
            backgroundColor: TABLE_PROGRESS_BG,
            '& .MuiLinearProgress-bar': { backgroundColor: hex, borderRadius: 99 },
          }}
        />
      </Box>

      {/* ── Risk level badge + matched source chip ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary">Risk Level:</Typography>
        <Chip
          label={label}
          size="small"
          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: hex + '22', color: hex, border: `1px solid ${hex}` }}
        />
        {matchedSolution?.source && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>·</Typography>
            <Typography variant="caption" fontWeight={600} color="text.secondary">Matched source:</Typography>
            <Chip
              label={sourceLabel}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
            />
          </>
        )}
      </Box>

      {/* ── Per-reference breakdown ── */}
      {breakdown?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', color: TABLE_LABEL, letterSpacing: 0.6 }}
          >
            Per-Reference Breakdown
          </Typography>
          <Box sx={{ border: `1px solid ${TABLE_BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
            {breakdown.map((b, i) => {
              const { hex: bHex } = deriveStatus(b.score);
              return (
                <Box
                  key={i}
                  sx={{
                    display: 'flex', gap: 2, alignItems: 'center', px: 1.5, py: 0.75,
                    borderBottom: i < breakdown.length - 1 ? `1px solid ${TABLE_BORDER}` : 'none',
                    '&:nth-of-type(even)': { bgcolor: TABLE_HEADER_BG },
                  }}
                >
                  <Chip label={b.source} size="small" variant="outlined" sx={{ textTransform: 'capitalize', minWidth: 80 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>{b.language}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Jaccard: <strong>{(b.jaccard * 100).toFixed(1)}%</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Dice: <strong>{(b.dice * 100).toFixed(1)}%</strong>
                  </Typography>
                  <Typography variant="caption" fontWeight={700} sx={{ color: bHex, ml: 'auto' }}>
                    {(b.score * 100).toFixed(1)}%
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Code comparison (react-diff-viewer-continued split view / fallback) ── */}
      <Box>
        <Typography
          variant="caption" fontWeight={700}
          sx={{ display: 'block', mb: 1, textTransform: 'uppercase', color: TABLE_LABEL, letterSpacing: 0.6 }}
        >
          Code Comparison
        </Typography>
        <SplitDiffView
          oldCode={studentCode}
          newCode={matchedSolution?.code}
          leftTitle="Student Code"
          rightTitle={`${sourceLabel} Reference Solution`}
          language={getPrismLanguage(studentLanguage)}
        />
      </Box>
    </Box>
  );
};

// ── Diff utilities (used as fallback when react-diff-viewer-continued is absent)
const computeLineDiff = (oldLines, newLines) => {
  const m = oldLines.length;
  const n = newLines.length;
  // Build LCS DP table
  const dp = Array.from({ length: m + 1 }, () => new Int32Array(n + 1));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] =
        oldLines[i - 1] === newLines[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
  // Backtrack to build diff
  const result = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      result.push({ type: 'equal',   value: oldLines[i - 1] }); i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.push({ type: 'added',   value: newLines[j - 1] }); j--;
    } else {
      result.push({ type: 'removed', value: oldLines[i - 1] }); i--;
    }
  }
  return result.reverse();
};

// ── DiffViewer (fallback when react-diff-viewer-continued is absent) ─────────
// Amber palette for fingerprint-match highlights ("copied blocks")
const FP_MATCH_COLOR       = '#f59e0b';
const FP_MATCH_BG          = 'rgba(245,158,11,0.16)';
const FP_MATCH_BORDER      = `3px solid ${FP_MATCH_COLOR}`;
const FP_MATCH_BORDER_NONE = '3px solid transparent';

/**
 * DiffViewer
 *
 * Unified diff view.  Lines in `matchedLinesLeft` / `matchedLinesRight` get
 * an amber \u25c4 indicator and background to mark Winnowing fingerprint matches.
 */
const DiffViewer = ({
  oldCode = '',
  newCode = '',
  matchedLinesLeft  = [],
  matchedLinesRight = [],
}) => {
  const oldLines      = (oldCode || '').split('\n');
  const newLines      = (newCode || '').split('\n');
  const diff          = computeLineDiff(oldLines, newLines);
  const matchLeftSet  = React.useMemo(() => new Set(matchedLinesLeft),  [matchedLinesLeft]);
  const matchRightSet = React.useMemo(() => new Set(matchedLinesRight), [matchedLinesRight]);
  const hasFpHighlights = matchedLinesLeft.length > 0 || matchedLinesRight.length > 0;

  let oldNo = 1;
  let newNo = 1;

  return (
    <Box
      sx={{
        fontFamily: '"Fira Code","Cascadia Code",Consolas,monospace',
        fontSize: '0.71rem',
        border: `1px solid ${TABLE_BORDER}`,
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      {/* Legend strip */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          px: 1.5,
          py: 0.6,
          marginBottom: 1,
          bgcolor: CODE_GUTTER_BG,
          borderBottom: `1px solid ${TABLE_BORDER}`,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: DIFF_REMOVED_TEXT, fontWeight: 700, letterSpacing: 0.4 }}>
          {'- This student'}
        </Typography>
        <Typography variant="caption" sx={{ color: DIFF_ADDED_TEXT, fontWeight: 700, letterSpacing: 0.4 }}>
          {'+ Peer'}
        </Typography>
        {hasFpHighlights && (
          <Typography variant="caption" sx={{ color: FP_MATCH_COLOR, fontWeight: 700, letterSpacing: 0.4, ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '2px', bgcolor: FP_MATCH_BG, border: `1px solid ${FP_MATCH_COLOR}`, verticalAlign: 'middle', mr: 0.3 }} />
            {'\u25c4 Fingerprint match (copied block)'}
          </Typography>
        )}
      </Box>

      {/* Diff body */}
      <Box
        sx={{
          maxHeight: 340,
          overflowY: 'auto',
          bgcolor: CODE_BG_DARK,
        }}
      >
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="tbody">
            {diff.map((entry, idx) => {
              const isRemoved = entry.type === 'removed';
              const isAdded   = entry.type === 'added';
              const thisOld   = !isAdded   ? oldNo++ : null;
              const thisNew   = !isRemoved ? newNo++ : null;

              const isFpMatch =
                (thisOld != null && matchLeftSet.has(thisOld)) ||
                (thisNew != null && matchRightSet.has(thisNew));

              const sign = isFpMatch
                ? '◄'
                : isRemoved ? '−' : isAdded ? '+' : ' ';

              return (
                <Box
                  key={idx}
                  component="tr"
                  sx={{
                    bgcolor: isFpMatch
                      ? FP_MATCH_BG
                      : isRemoved
                      ? DIFF_REMOVED_BG
                      : isAdded
                      ? DIFF_ADDED_BG
                      : 'transparent',
                    borderLeft: isFpMatch ? FP_MATCH_BORDER : FP_MATCH_BORDER_NONE,
                  }}
                >
                  {/* Old line# */}
                  <Box
                    component="td"
                    sx={{
                      width: 34, minWidth: 34, textAlign: 'right',
                      px: 0.75, userSelect: 'none',
                      color: isFpMatch ? FP_MATCH_COLOR : isRemoved ? DIFF_REMOVED_TEXT : DIFF_NEUTRAL_TEXT,
                      fontWeight: isFpMatch ? 700 : 400,
                      borderRight: `1px solid ${TABLE_BORDER}`,
                    }}
                  >
                    {thisOld ?? ''}
                  </Box>
                  {/* New line# */}
                  <Box
                    component="td"
                    sx={{
                      width: 34, minWidth: 34, textAlign: 'right',
                      px: 0.75, userSelect: 'none',
                      color: isFpMatch ? FP_MATCH_COLOR : isAdded ? DIFF_ADDED_TEXT : DIFF_NEUTRAL_TEXT,
                      fontWeight: isFpMatch ? 700 : 400,
                      borderRight: `1px solid ${TABLE_BORDER}`,
                    }}
                  >
                    {thisNew ?? ''}
                  </Box>
                  {/* ± / ◄ indicator */}
                  <Box
                    component="td"
                    sx={{
                      width: 18, textAlign: 'center', userSelect: 'none',
                      fontWeight: 700,
                      color: isFpMatch ? FP_MATCH_COLOR : isRemoved ? DIFF_REMOVED_TEXT : isAdded ? DIFF_ADDED_TEXT : DIFF_NEUTRAL_TEXT,
                      borderRight: `1px solid ${TABLE_BORDER}`,
                    }}
                  >
                    {sign}
                  </Box>
                  {/* Content */}
                  <Box
                    component="td"
                    sx={{
                      pl: 1, pr: 1.5,
                      whiteSpace: 'pre', overflowX: 'auto',
                      color: isFpMatch
                        ? '#fff'
                        : isRemoved ? DIFF_REMOVED_TEXT_LIGHT : isAdded ? DIFF_ADDED_TEXT_LIGHT : DIFF_NEUTRAL_TEXT_DARK,
                      width: '100%',
                    }}
                  >
                    {entry.value}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};


const RDVC_DARK = {
  diffViewerBackground:     CODE_BG_DARK,
  diffViewerColor:          DIFF_NEUTRAL_TEXT_DARK,
  addedBackground:          DIFF_ADDED_BG,
  addedGutterBackground:    DIFF_ADDED_GUTTER_BG,
  removedBackground:        DIFF_REMOVED_BG,
  removedGutterBackground:  DIFF_REMOVED_GUTTER_BG,
  wordAddedBackground:      DIFF_WORD_ADDED_BG,
  wordRemovedBackground:    DIFF_WORD_REMOVED_BG,
  addedGutterColor:         DIFF_ADDED_TEXT,
  removedGutterColor:       DIFF_REMOVED_TEXT,
  gutterBackground:         CODE_GUTTER_BG,
  gutterBackgroundDark:     CODE_GUTTER_BG_DARK,
  codeFoldBackground:       CODE_FOLD_BG,
  emptyLineBackground:      CODE_BG_DARK,
  gutterColor:              CODE_GUTTER_COLOR,
  addedColor:               DIFF_ADDED_TEXT_LIGHT,
  removedColor:             DIFF_REMOVED_TEXT_LIGHT,
  codeFoldContentColor:     TABLE_LABEL,
  diffViewerTitleBackground: CODE_GUTTER_BG,
  diffViewerTitleColor:     TABLE_MUTED,
  diffViewerTitleBorderColor: TABLE_BORDER,
};

const SplitDiffView = ({
  oldCode = '',
  newCode = '',
  leftTitle = 'Student Code',
  rightTitle = 'Reference',
  language = 'plaintext',
  matchedLinesLeft  = [],
  matchedLinesRight = [],
}) => {
  if (ReactDiffViewer) {
    return (
      <Box
        sx={{
          borderRadius: 1.5,
          overflow: 'hidden',
          border: `1px solid ${TABLE_BORDER}`,
          // Narrow the font inside the diff table
          '& table': {
            fontSize: '0.71rem !important',
            fontFamily: '"Fira Code","Cascadia Code",Consolas,monospace !important',
          },
        }}
      >
        <ReactDiffViewer
          oldValue={oldCode || ''}
          newValue={newCode || ''}
          splitView
          useDarkTheme
          leftTitle={leftTitle}
          rightTitle={rightTitle}
          showDiffOnly={false}
          styles={{
            variables:  { dark: RDVC_DARK },
            titleBlock: { padding: '0px 0px 0px 16px', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1.5 },
          }}
        />
      </Box>
    );
  }

  // ── Fallback: CodeBlock pair + custom line-diff ──
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, mb: 2 }}>
        <CodeBlock title={leftTitle}  code={oldCode} language={language} accent={INDIGO} />
        <CodeBlock title={rightTitle} code={newCode} language={language} accent={TABLE_BORDER} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
        <Typography
          variant="caption" fontWeight={700}
          sx={{ textTransform: 'uppercase', color: TABLE_LABEL, letterSpacing: 0.6 }}
        >
          Diff View
        </Typography>
        <Typography variant="caption" color="text.disabled">
          red = unique to left &middot; green = unique to right
        </Typography>
      </Box>
      <DiffViewer
        oldCode={oldCode}
        newCode={newCode}
        matchedLinesLeft={matchedLinesLeft}
        matchedLinesRight={matchedLinesRight}
      />
    </>
  );
};

// ── PeerSimilarityBar ──────────────────────────────────────────────────
const PeerSimilarityBar = ({ score, plagiarismStatus }) => {
  const pct       = (score * 100).toFixed(1);
  const { hex }   = deriveStatus(score, plagiarismStatus);
  return (
    <Tooltip
      title={`Similarity: ${pct}%`}
      placement="top"
      arrow
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'help', minWidth: 130 }}>
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(score * 100, 100)}
            sx={{
              height: 7,
              borderRadius: 99,
              backgroundColor: TABLE_PROGRESS_BG,
              '& .MuiLinearProgress-bar': { backgroundColor: hex, borderRadius: 99 },
            }}
          />
        </Box>
        <Typography variant="body2" fontWeight={600} sx={{ color: hex, minWidth: 44 }}>
          {pct}%
        </Typography>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: TABLE_MUTED, flexShrink: 0 }} />
      </Box>
    </Tooltip>
  );
};

// ── PeerRow (expandable) ─────────────────────────────────────────────────
const PeerRow = ({ row, focalCode, focalLanguage }) => {
  const [open, setOpen] = React.useState(false);
  const { hex, label }  = deriveStatus(row.similarity ?? 0, row.plagiarismStatus);
  const peerCode        = row.studentCode;
  const hasCode         = Boolean(peerCode || focalCode);
  const peer = row.student === row.studentA ? row.studentB : row.studentA;
  const peerName        = peer === row.studentA ? row.studentBName : row.studentAName;
  const userName = peer === row.studentA ? row.studentAName : row.studentBName;

  return (
    <React.Fragment>
      {/* Main data row */}
      <TableRow
        hover
        sx={{
          cursor: hasCode ? 'pointer' : 'default',
          bgcolor: open ? TABLE_HEADER_BG : undefined,
          '& td': { borderBottom: open ? 'none' : undefined },
        }}
        onClick={() => hasCode && setOpen((v) => !v)}
      >
        {/* Expand toggle */}
        <TableCell sx={{ width: 44, pr: 0 }}>
          {hasCode && (
            <Tooltip title={open ? 'Collapse' : 'Expand comparison'} placement="right">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
              >
                {open
                  ? <KeyboardArrowUpIcon fontSize="small" />
                  : <KeyboardArrowDownIcon fontSize="small" />
                }
              </IconButton>
            </Tooltip>
          )}
        </TableCell>

        {/* Student name */}
        <TableCell>
          <Typography variant="body2" fontWeight={500}>{peerName}</Typography>
        </TableCell>

        {/* Similarity */}
        <TableCell sx={{ minWidth: 160 }}>
          <PeerSimilarityBar score={row.similarity ?? 0} plagiarismStatus={row.plagiarismStatus} />
        </TableCell>

        {/* Status */}
        <TableCell align="center">
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={label}
              size="small"
              sx={{
                height: 22, fontSize: 11, fontWeight: 700,
                bgcolor: hex + '22', color: hex, border: `1px solid ${hex}`,
              }}
            />
            {row.isFlagged && (
              <Tooltip title="Flagged for review">
                <FlagIcon sx={{ fontSize: 15, color: PLAGIARISM_HIGH_HEX }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>

      {/* Expandable detail */}
      {hasCode && (
        <TableRow sx={{ bgcolor: TABLE_HEADER_BG }}>
          <TableCell colSpan={4} sx={{ py: 0, px: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ px: 2, pt: 1.5, pb: 2.5 }}>
                <Divider sx={{ mb: 1.5 }} />

                {/* ── Code comparison + diff (SplitDiffView) ── */}
                <Typography
                  variant="caption" fontWeight={700}
                  sx={{ textTransform: 'uppercase', color: TABLE_LABEL, letterSpacing: 0.6, display: 'block', mb: 1 }}
                >
                  Code Comparison
                </Typography>
                <SplitDiffView
                  oldCode={focalCode}
                  newCode={peerCode}
                  leftTitle={`${userName}'s Code`}
                  rightTitle={`${peerName}'s Code`}
                  language={getPrismLanguage(focalLanguage)}
                  matchedLinesLeft={row.matchedLines?.left ?? []}
                  matchedLinesRight={row.matchedLines?.right ?? []}
                />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};


const PeerComparisonTable = ({ rows = [], focalCode = '', focalLanguage = '' }) => {

  console.log('Rendering PeerComparisonTable with rows:', rows);
  const [order, setOrder]     = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('similarity');

  const handleSort = (field) => {
    setOrder((o) => (orderBy === field && o === 'desc' ? 'asc' : 'desc'));
    setOrderBy(field);
  };

  const sorted = [...rows].sort((a, b) => {
    const av = a[orderBy] ?? 0;
    const bv = b[orderBy] ?? 0;
    return order === 'desc' ? (bv > av ? 1 : bv < av ? -1 : 0) : (av > bv ? 1 : av < bv ? -1 : 0);
  });

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No similar student submissions found for this problem.
        </Typography>
      </Box>
    );
  }

  const SortHead = ({ field, label, align = 'left' }) => (
    <TableCell align={align} sx={{ fontWeight: 700, color: TABLE_HEADER_TEXT }}>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? order : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: `1px solid ${TABLE_BORDER}`,
        borderRadius: '0 0 8px 8px',
        bgcolor: panelSurfaceBg,
        backgroundImage: 'none',
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: TABLE_HEADER_BG }}>
            <TableCell sx={{ width: 44 }} />
            <SortHead field="student"         label="Student" />
            <SortHead field="similarity"      label="Similarity Score" />
            <SortHead field="plagiarismStatus" label="Status" align="center" />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, i) => (
            <PeerRow
              key={row.id ?? i}
              row={row}
              focalCode={focalCode}
              focalLanguage={focalLanguage}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};


const SolutionPreviewPanel = ({
  open,
  onClose,
  submission,
  loading = false,
  plagiarismReport = null,
  peerRows = [],
}) => {
  const theme = useTheme();
  const prismStyle = theme.palette.mode === 'dark' ? atomDark : oneLight;
  const codeGutterColor =
    theme.palette.mode === 'dark' ? CODE_GUTTER_COLOR : 'rgba(15, 23, 42, 0.45)';

  const isAccepted = submission?.status === true;
  const peerCount  = peerRows?.length ?? 0;


  // ── Internal plagiarism-detail fetch ────────────────────────────────────
  const [internalReport, setInternalReport] = React.useState(null);

  React.useEffect(() => {
    setInternalReport(null);
    if (!open || !submission?.studentId || !submission?.problemSlug || plagiarismReport) return;
    setInternalReport(plagiarismReport);
  }, [open, submission?.studentId, submission?.problemSlug, plagiarismReport]);

  const effectiveReport = plagiarismReport ?? internalReport;

  const aiStatus = effectiveReport
    ? deriveStatus(effectiveReport.similarity ?? 0, effectiveReport.plagiarismStatus)
    : null;

  // Portals to document.body so position:fixed is viewport-relative. MUI DataGrid (and
  // other ancestors with transform/filter) otherwise create a containing block and the
  // panel renders clipped inside the grid.
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          bgcolor: alpha(
            theme.palette.common.black,
            theme.palette.mode === 'dark' ? 0.72 : 0.5
          ),
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 250ms ease",
        }}
      />

      {/* Slide panel */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: PANEL_WIDTH,
          height: "100vh",
          zIndex: 1201,
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <Slide
          direction="left"
          in={open}
          timeout={250}
          mountOnEnter
          unmountOnExit
        >
          <Paper
            elevation={0}
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: PANEL_WIDTH,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: "12px 0 0 12px",
              boxShadow: PANEL_SHADOW,
              overflow: "hidden",
              backgroundImage: "none",
              bgcolor: panelSurfaceBg,
            }}
          >
            {/* ── Panel header ── */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 1.75,
                background: (t) =>
                  t.palette.mode === "dark"
                    ? TOOLBAR_BG_DARK
                    : TOOLBAR_BG_LIGHT,
                borderBottom: "1px solid",
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {!loading &&
                  submission &&
                  (isAccepted ? (
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  ) : (
                    <ErrorOutlineIcon fontSize="small" color="error" />
                  ))}
                <Typography variant="subtitle1" fontWeight={700}>
                  Solution Preview
                </Typography>
              </Box>

              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? PANEL_CLOSE_BTN_BG
                      : INDIGO_LIGHT,
                  "&:hover": {
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? PANEL_CLOSE_BTN_HOVER
                        : INDIGO_LIGHTER,
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* ── Scrollable accordion body ── */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                bgcolor: panelSurfaceBg,
              }}
            >
              {/* Loading state */}
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                  <CircularProgress />
                </Box>
              )}

              {/* Empty state */}
              {!loading && !submission && (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  No submission data found.
                </Typography>
              )}

              {/* ── Accordion layout ── */}
              {!loading && submission && (
                <Box>
                  {/* Problem title + meta above accordions */}
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {submission.problemTitle ?? submission.problemSlug}
                    </Typography>
                    {submission.studentName && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        mt={0.5}
                      >
                        {submission.studentName}
                      </Typography>
                    )}
                    {submission.submittedAt && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={0.5}
                      >
                        Submitted:{" "}
                        {new Date(submission.submittedAt).toLocaleString()}
                      </Typography>
                    )}
                  </Box>

                  {/* ───────────────────────────────────────────────────── */}
                  {/* 1️⃣  Student Submission  (expanded by default)         */}
                  {/* ───────────────────────────────────────────────────── */}
                  <Accordion defaultExpanded disableGutters sx={panelAccordionSx}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={accordionSummarySx}
                    >
                      <SectionHeader
                        icon={
                          <CodeIcon fontSize="small" sx={{ color: INDIGO }} />
                        }
                        title="Student Submission"
                        badge={
                          <Chip
                            label={isAccepted ? "Accepted" : "Rejected"}
                            size="small"
                            color={isAccepted ? "success" : "error"}
                            sx={{ fontWeight: 700, height: 20, fontSize: 11 }}
                          />
                        }
                      />
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{ px: 2, pt: 0, pb: 2, bgcolor: panelSurfaceBg }}
                    >
                      {/* Language / runtime / memory chips */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        {submission.language && (
                          <Chip
                            label={submission.language}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {submission.runtime && (
                          <Chip
                            label={`⏱ ${submission.runtime}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {submission.memory && (
                          <Chip
                            label={`💾 ${submission.memory}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {/* Syntax-highlighted code */}
                      <Box
                        sx={{
                          maxHeight: 480,
                          overflowY: "auto",
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow:
                            theme.palette.mode === 'dark' ? 'none' : CODE_BLOCK_SHADOW,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? 'transparent'
                              : 'grey.50',
                        }}
                      >
                        <SyntaxHighlighter
                          language={getPrismLanguage(submission.language)}
                          style={prismStyle}
                          showLineNumbers
                          wrapLongLines
                          customStyle={{
                            margin: 0,
                            borderRadius: 6,
                            fontSize: "0.78rem",
                          }}
                          lineNumberStyle={{
                            color: codeGutterColor,
                            minWidth: "2.2em",
                          }}
                        >
                          {submission.code ?? "// No code available."}
                        </SyntaxHighlighter>
                      </Box>

                      {/* Notes */}
                      {submission.notes && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                              textTransform: "uppercase",
                              color: TABLE_LABEL,
                              letterSpacing: 0.6,
                              display: "block",
                              mb: 0.5,
                            }}
                          >
                            Notes
                          </Typography>
                          <Typography variant="body2">
                            {submission.notes}
                          </Typography>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* ───────────────────────────────────────────────────── */}
                  {/* 2️⃣  Plagiarism Analysis Report                        */}
                  {/* ───────────────────────────────────────────────────── */}
                  <Accordion disableGutters sx={panelAccordionSx}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={accordionSummarySx}
                    >
                      <SectionHeader
                        icon={
                          <SecurityIcon
                            fontSize="small"
                            sx={{
                              color: aiStatus ? aiStatus.hex : TABLE_MUTED,
                            }}
                          />
                        }
                        title="Plagiarism Analysis Report"
                        badge={
                          aiStatus && (
                            <Chip
                              label={aiStatus.label}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 11,
                                fontWeight: 700,
                                bgcolor: aiStatus.hex + "22",
                                color: aiStatus.hex,
                                border: `1px solid ${aiStatus.hex}`,
                              }}
                            />
                          )
                        }
                      />
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                      <AISimilarityReport
                        report={effectiveReport}
                        studentCode={submission.code}
                        studentLanguage={submission.language}
                      />
                    </AccordionDetails>
                  </Accordion>

                  {/* ───────────────────────────────────────────────────── */}
                  {/* 3️⃣  Similar Student Submissions                      */}
                  {/* ───────────────────────────────────────────────────── */}
                  <Accordion disableGutters sx={panelAccordionSx}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={accordionSummarySx}
                    >
                      <SectionHeader
                        icon={
                          <GroupIcon
                            fontSize="small"
                            sx={{
                              color:
                                peerCount > 0
                                  ? PLAGIARISM_MEDIUM_HEX
                                  : TABLE_MUTED,
                            }}
                          />
                        }
                        title="Matched Students"
                        badge={
                          peerCount > 0 && (
                            <Chip
                              label={`${peerCount} match${peerCount !== 1 ? "es" : ""}`}
                              size="small"
                              color="warning"
                              sx={{ fontWeight: 700, height: 20, fontSize: 11 }}
                            />
                          )
                        }
                      />
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0, bgcolor: panelSurfaceBg }}>
                      <PeerComparisonTable
                        rows={peerRows}
                        focalCode={submission.code}
                        focalLanguage={submission.language}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
            </Box>
          </Paper>
        </Slide>
      </Box>
    </>,
    document.body
  );
};

export default SolutionPreviewPanel;
